/**
 * XP Service - Handles experience points, leagues, and gamification
 */

import { supabase } from './supabase';

export type XPAction = 'open_card' | 'learned_word' | 'correct_answer';

export interface XPResult {
  xp_earned: number;
  total_xp: number;
  weekly_xp: number;
  doubled: boolean;
  boosted: boolean;
}

export interface WeeklyEvaluationResult {
  streak_changed: number;
  league_changed: number;
  new_league_name: string;
  message: string;
}

export interface LeagueProgress {
  league_name: string;
  league_emoji: string;
  league_order: number;
  current_streak: number;
  min_streak_required: number;
  weekly_xp: number;
  weekly_goal: number;
  progress_percentage: number;
  can_promote: boolean;
  at_risk_demote: boolean;
  next_league_name: string | null;
  comeback_boost_active: boolean;
}

export interface League {
  id: number;
  name: string;
  emoji: string;
  min_streak_weeks: number;
  xp_goal_weekly: number;
  order: number;
}

/**
 * XP Service class
 */
export class XPService {
  /**
   * Add XP for a user action
   */
  static async addXP(
    userId: string,
    action: XPAction,
    metadata: Record<string, any> = {}
  ): Promise<XPResult | null> {
    try {
      const { data, error } = await supabase.rpc('add_xp', {
        p_user_id: userId,
        p_action: action,
        p_metadata: metadata,
      });

      if (error) {
        console.error('Error adding XP:', error);
        return null;
      }

      // RPC returns array with single row
      return data?.[0] || null;
    } catch (err) {
      console.error('Exception adding XP:', err);
      return null;
    }
  }

  /**
   * Evaluate weekly progress and update league
   */
  static async evaluateWeek(userId: string): Promise<WeeklyEvaluationResult | null> {
    try {
      const { data, error } = await supabase.rpc('evaluate_week', {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error evaluating week:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (err) {
      console.error('Exception evaluating week:', err);
      return null;
    }
  }

  /**
   * Get user's league progress
   */
  static async getLeagueProgress(userId: string): Promise<LeagueProgress | null> {
    try {
      const { data, error } = await supabase.rpc('get_league_progress', {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error getting league progress:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (err) {
      console.error('Exception getting league progress:', err);
      return null;
    }
  }

  /**
   * Get all leagues
   */
  static async getAllLeagues(): Promise<League[]> {
    try {
      const { data, error } = await supabase
        .from('leagues')
        .select('*')
        .order('order', { ascending: true });

      if (error) {
        console.error('Error getting leagues:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Exception getting leagues:', err);
      return [];
    }
  }

  /**
   * Get weekly XP for current week
   */
  static async getWeeklyXP(userId: string): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('get_weekly_xp', {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error getting weekly XP:', error);
        return 0;
      }

      return data || 0;
    } catch (err) {
      console.error('Exception getting weekly XP:', err);
      return 0;
    }
  }

  /**
   * Get XP log for user (recent entries)
   */
  static async getXPLog(userId: string, limit: number = 50): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('user_xp_log')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting XP log:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Exception getting XP log:', err);
      return [];
    }
  }

  /**
   * Calculate XP preview (what user would get for an action)
   */
  static calculateXPPreview(
    action: XPAction,
    currentWeeklyXP: number,
    weeklyGoal: number,
    comebackBoost: boolean
  ): number {
    const baseXP: Record<XPAction, number> = {
      open_card: 1,
      learned_word: 5,
      correct_answer: 2,
    };

    let xp = baseXP[action];

    // Double if over goal
    if (currentWeeklyXP >= weeklyGoal) {
      xp *= 2;
    }

    // Comeback boost
    if (comebackBoost) {
      xp = Math.floor(xp * 1.5);
    }

    return xp;
  }

  /**
   * Format XP number for display
   */
  static formatXP(xp: number): string {
    return xp.toLocaleString('cs-CZ');
  }

  /**
   * Get days until week end
   */
  static getDaysUntilWeekEnd(): number {
    const now = new Date();
    const dayOfWeek = now.getDay();
    // Sunday = 0, Monday = 1, ..., Saturday = 6
    // Calculate days until Sunday (0)
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    return daysUntilSunday;
  }

  /**
   * Check if in grace period (Monday or Tuesday)
   */
  static isInGracePeriod(): boolean {
    const now = new Date();
    const dayOfWeek = now.getDay();
    return dayOfWeek === 1 || dayOfWeek === 2; // Monday or Tuesday
  }
}
